import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  BookOpen, 
  LogOut, 
  Mail, 
  MailOpen, 
  Trash2, 
  RefreshCw,
  Inbox,
  Clock,
  Users,
  Settings,
  Shield,
  UserPlus,
  Key,
  Home,
  BarChart3,
  Crown
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({ total_messages: 0, unread_messages: 0, admin_count: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("messages");
  
  // Modal states
  const [showNewAdmin, setShowNewAdmin] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newAdminData, setNewAdminData] = useState({ username: "", password: "", is_super_admin: false });
  const [passwordData, setPasswordData] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("mindary_token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const [messagesRes, statsRes, meRes] = await Promise.all([
        axios.get(`${API}/messages`, { headers: getAuthHeaders() }),
        axios.get(`${API}/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/me`, { headers: getAuthHeaders() })
      ]);
      setMessages(messagesRes.data);
      setStats(statsRes.data);
      setCurrentUser(meRes.data);
      
      // Fetch admins if super admin
      if (meRes.data.is_super_admin) {
        const adminsRes = await axios.get(`${API}/admins`, { headers: getAuthHeaders() });
        setAdmins(adminsRes.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Sesjonen har utløpt", {
          description: "Vennligst logg inn igjen."
        });
        localStorage.removeItem("mindary_token");
        navigate("/admin");
      } else {
        toast.error("Kunne ikke hente data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("mindary_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate]);

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.patch(`${API}/messages/${messageId}/read`, {}, { headers: getAuthHeaders() });
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
      setStats({ ...stats, unread_messages: stats.unread_messages - 1 });
      toast.success("Merket som lest");
    } catch (error) {
      toast.error("Kunne ikke oppdatere melding");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`, { headers: getAuthHeaders() });
      const deletedMessage = messages.find(m => m.id === messageId);
      setMessages(messages.filter(m => m.id !== messageId));
      setStats({ 
        ...stats, 
        total_messages: stats.total_messages - 1,
        unread_messages: deletedMessage && !deletedMessage.read 
          ? stats.unread_messages - 1 
          : stats.unread_messages
      });
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      toast.success("Melding slettet");
    } catch (error) {
      toast.error("Kunne ikke slette melding");
    }
  };

  const handleCreateAdmin = async () => {
    if (newAdminData.password.length < 6) {
      toast.error("Passord må være minst 6 tegn");
      return;
    }
    try {
      const res = await axios.post(`${API}/admins`, newAdminData, { headers: getAuthHeaders() });
      setAdmins([...admins, res.data]);
      setStats({ ...stats, admin_count: stats.admin_count + 1 });
      setShowNewAdmin(false);
      setNewAdminData({ username: "", password: "", is_super_admin: false });
      toast.success("Admin opprettet!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke opprette admin");
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    try {
      await axios.delete(`${API}/admins/${adminId}`, { headers: getAuthHeaders() });
      setAdmins(admins.filter(a => a.id !== adminId));
      setStats({ ...stats, admin_count: stats.admin_count - 1 });
      toast.success("Admin slettet");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke slette admin");
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passordene stemmer ikke overens");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("Nytt passord må være minst 6 tegn");
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { headers: getAuthHeaders() });
      setShowChangePassword(false);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Passord endret!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke endre passord");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("mindary_token");
    navigate("/admin");
    toast.success("Du er logget ut");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("no-NO", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <BookOpen className="w-6 h-6 text-primary" />
          </div>
          <p className="text-muted-foreground">Laster...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border/50 hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3 mb-8" data-testid="dashboard-logo">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <span className="font-display text-lg font-semibold text-foreground block">
                mindary
              </span>
              <span className="text-xs text-muted-foreground">Admin Panel</span>
            </div>
          </Link>

          {/* User Info */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                {currentUser?.is_super_admin ? (
                  <Crown className="w-5 h-5 text-primary" />
                ) : (
                  <Users className="w-5 h-5 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate" data-testid="current-user">
                  {currentUser?.username}
                </p>
                <p className="text-xs text-muted-foreground">
                  {currentUser?.is_super_admin ? "Super Admin" : "Admin"}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab("messages")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === "messages" 
                  ? "bg-primary text-primary-foreground" 
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
              data-testid="nav-messages"
            >
              <Mail className="w-5 h-5" />
              <span>Meldinger</span>
              {stats.unread_messages > 0 && (
                <Badge variant={activeTab === "messages" ? "secondary" : "default"} className="ml-auto">
                  {stats.unread_messages}
                </Badge>
              )}
            </button>

            {currentUser?.is_super_admin && (
              <button
                onClick={() => setActiveTab("admins")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === "admins" 
                    ? "bg-primary text-primary-foreground" 
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
                data-testid="nav-admins"
              >
                <Shield className="w-5 h-5" />
                <span>Administratorer</span>
              </button>
            )}

            <button
              onClick={() => setActiveTab("settings")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === "settings" 
                  ? "bg-primary text-primary-foreground" 
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
              data-testid="nav-settings"
            >
              <Settings className="w-5 h-5" />
              <span>Innstillinger</span>
            </button>
          </nav>
        </div>

        {/* Bottom actions */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border/50">
          <div className="space-y-2">
            <Link to="/" className="flex items-center gap-3 px-4 py-2 text-muted-foreground hover:text-foreground transition-colors">
              <Home className="w-4 h-4" />
              <span className="text-sm">Til forsiden</span>
            </Link>
            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-muted-foreground hover:text-destructive transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Logg ut</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-display font-semibold text-foreground">mindary</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto">
          <Button 
            variant={activeTab === "messages" ? "default" : "outline"} 
            size="sm" 
            onClick={() => setActiveTab("messages")}
            className="rounded-full"
          >
            <Mail className="w-4 h-4 mr-1" /> Meldinger
          </Button>
          {currentUser?.is_super_admin && (
            <Button 
              variant={activeTab === "admins" ? "default" : "outline"} 
              size="sm" 
              onClick={() => setActiveTab("admins")}
              className="rounded-full"
            >
              <Shield className="w-4 h-4 mr-1" /> Admins
            </Button>
          )}
          <Button 
            variant={activeTab === "settings" ? "default" : "outline"} 
            size="sm" 
            onClick={() => setActiveTab("settings")}
            className="rounded-full"
          >
            <Settings className="w-4 h-4 mr-1" /> Innstillinger
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 lg:p-8">
          {/* Stats Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          >
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Inbox className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Totalt</p>
                    <p className="text-xl font-semibold text-foreground" data-testid="total-messages">{stats.total_messages}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Uleste</p>
                    <p className="text-xl font-semibold text-foreground" data-testid="unread-messages">{stats.unread_messages}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                    <Users className="w-5 h-5 text-secondary-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Admins</p>
                    <p className="text-xl font-semibold text-foreground" data-testid="admin-count">{stats.admin_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Lest</p>
                    <p className="text-xl font-semibold text-foreground">{stats.total_messages - stats.unread_messages}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Messages Tab */}
          {activeTab === "messages" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid lg:grid-cols-5 gap-6"
            >
              {/* Message List */}
              <div className="lg:col-span-2">
                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-display flex items-center gap-2">
                      <Mail className="w-5 h-5 text-primary" />
                      Meldinger
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={fetchData} data-testid="refresh-button">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[calc(100vh-380px)]">
                      {messages.length === 0 ? (
                        <div className="p-6 text-center text-muted-foreground">
                          <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>Ingen meldinger ennå</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-border/50">
                          <AnimatePresence>
                            {messages.map((message) => (
                              <motion.div
                                key={message.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                                  selectedMessage?.id === message.id ? "bg-muted/50" : ""
                                } ${!message.read ? "border-l-4 border-l-primary" : ""}`}
                                onClick={() => setSelectedMessage(message)}
                                data-testid={`message-item-${message.id}`}
                              >
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <p className={`font-medium text-foreground truncate ${!message.read ? "font-semibold" : ""}`}>
                                    {message.name}
                                  </p>
                                  {!message.read && (
                                    <Badge variant="default" className="text-xs shrink-0">Ny</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground truncate mb-1">
                                  {message.email}
                                </p>
                                <p className="text-sm text-muted-foreground/70 line-clamp-2">
                                  {message.message}
                                </p>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Message Detail */}
              <div className="lg:col-span-3">
                <Card className="bg-card border-border/50 h-[calc(100vh-380px)]">
                  <CardContent className="p-6 h-full">
                    {selectedMessage ? (
                      <div className="h-full flex flex-col">
                        <div className="flex items-start justify-between mb-6">
                          <div>
                            <h2 className="font-display text-xl font-semibold text-foreground mb-1" data-testid="selected-message-name">
                              {selectedMessage.name}
                            </h2>
                            <p className="text-sm text-muted-foreground">{selectedMessage.email}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground/70">
                              <Clock className="w-3 h-3" />
                              {formatDate(selectedMessage.created_at)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!selectedMessage.read && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkAsRead(selectedMessage.id)}
                                className="rounded-full"
                                data-testid="mark-read-button"
                              >
                                <MailOpen className="w-4 h-4 mr-2" />
                                Merk som lest
                              </Button>
                            )}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="rounded-full"
                                  data-testid="delete-message-button"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Er du sikker?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Denne handlingen kan ikke angres. Meldingen vil bli permanent slettet.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteMessage(selectedMessage.id)}>
                                    Slett
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                        <ScrollArea className="flex-1">
                          <div className="bg-muted/30 rounded-xl p-6">
                            <p className="text-foreground whitespace-pre-wrap leading-relaxed" data-testid="selected-message-content">
                              {selectedMessage.message}
                            </p>
                          </div>
                        </ScrollArea>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center text-center text-muted-foreground">
                        <div>
                          <MailOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                          <p>Velg en melding for å se innholdet</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          )}

          {/* Admins Tab - Only for Super Admin */}
          {activeTab === "admins" && currentUser?.is_super_admin && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Card className="bg-card border-border/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-display flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      Administratorer
                    </CardTitle>
                    <CardDescription>Administrer brukere med tilgang til admin-panelet</CardDescription>
                  </div>
                  <Dialog open={showNewAdmin} onOpenChange={setShowNewAdmin}>
                    <DialogTrigger asChild>
                      <Button className="rounded-full" data-testid="add-admin-button">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Ny admin
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Opprett ny admin</DialogTitle>
                        <DialogDescription>
                          Fyll ut informasjonen for den nye administratoren
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="new-username">Brukernavn</Label>
                          <Input
                            id="new-username"
                            value={newAdminData.username}
                            onChange={(e) => setNewAdminData({ ...newAdminData, username: e.target.value })}
                            placeholder="brukernavn"
                            data-testid="new-admin-username"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="new-password">Passord</Label>
                          <Input
                            id="new-password"
                            type="password"
                            value={newAdminData.password}
                            onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                            placeholder="••••••••"
                            data-testid="new-admin-password"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is-super-admin"
                            checked={newAdminData.is_super_admin}
                            onCheckedChange={(checked) => setNewAdminData({ ...newAdminData, is_super_admin: checked })}
                            data-testid="new-admin-super"
                          />
                          <Label htmlFor="is-super-admin" className="text-sm">
                            Super Admin (kan administrere andre admins)
                          </Label>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNewAdmin(false)}>Avbryt</Button>
                        <Button onClick={handleCreateAdmin} data-testid="create-admin-button">Opprett</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {admins.map((admin) => (
                      <div 
                        key={admin.id} 
                        className="flex items-center justify-between p-4 bg-muted/30 rounded-xl"
                        data-testid={`admin-item-${admin.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            admin.is_super_admin ? "bg-primary/20" : "bg-muted"
                          }`}>
                            {admin.is_super_admin ? (
                              <Crown className="w-5 h-5 text-primary" />
                            ) : (
                              <Users className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{admin.username}</p>
                            <p className="text-xs text-muted-foreground">
                              {admin.is_super_admin ? "Super Admin" : "Admin"} • Opprettet {formatDate(admin.created_at)}
                            </p>
                          </div>
                        </div>
                        {admin.id !== currentUser?.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Slett admin?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Er du sikker på at du vil slette {admin.username}? Denne handlingen kan ikke angres.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteAdmin(admin.id)}>
                                  Slett
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="max-w-2xl"
            >
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <Settings className="w-5 h-5 text-primary" />
                    Innstillinger
                  </CardTitle>
                  <CardDescription>Administrer kontoen din</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Profile Info */}
                  <div className="p-4 bg-muted/30 rounded-xl">
                    <h3 className="font-medium text-foreground mb-3">Profil</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Brukernavn</span>
                        <span className="font-medium text-foreground">{currentUser?.username}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Rolle</span>
                        <Badge variant={currentUser?.is_super_admin ? "default" : "secondary"}>
                          {currentUser?.is_super_admin ? "Super Admin" : "Admin"}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Change Password */}
                  <div className="p-4 bg-muted/30 rounded-xl">
                    <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Sikkerhet
                    </h3>
                    <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full" data-testid="change-password-button">
                          Endre passord
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Endre passord</DialogTitle>
                          <DialogDescription>
                            Skriv inn nåværende passord og velg et nytt
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="current-password">Nåværende passord</Label>
                            <Input
                              id="current-password"
                              type="password"
                              value={passwordData.current_password}
                              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                              data-testid="current-password-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-password">Nytt passord</Label>
                            <Input
                              id="new-password"
                              type="password"
                              value={passwordData.new_password}
                              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                              data-testid="new-password-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="confirm-password">Bekreft nytt passord</Label>
                            <Input
                              id="confirm-password"
                              type="password"
                              value={passwordData.confirm_password}
                              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                              data-testid="confirm-password-input"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowChangePassword(false)}>Avbryt</Button>
                          <Button onClick={handleChangePassword} data-testid="save-password-button">Lagre</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
